import { supabase } from '../integrations/supabase/client';
import { getCurrentTenantSlug } from '../utils/tenant-utils';

export interface ProjectCreationResult {
  success: boolean;
  project?: any;
  error?: string;
  canCreate: boolean;
  currentProjects: number;
  maxProjects: number;
  planName: string;
}

export class ProjectService {
  /**
   * Check if workspace can create more projects based on plan limits
   */
  static async canCreateProject(workspaceId: string): Promise<{
    canCreate: boolean;
    currentProjects: number;
    maxProjects: number;
    planName: string;
  }> {
    try {
      // Get workspace limits
      const { data: limits, error } = await (supabase as any)
        .rpc('get_workspace_limits_with_usage', { workspace_uuid: workspaceId });

      if (error || !limits || limits.length === 0) {
        throw new Error('Failed to get workspace limits');
      }

      const limit = limits[0];
      const maxProjects = limit.max_projects || 0;

      // Count current projects directly from products table
      const { count: currentProjects, error: countError } = await (supabase as any)
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('workspace_id', workspaceId);

      if (countError) {
        throw new Error('Failed to count current projects');
      }

      // Check if we can create more projects
      const canCreate = maxProjects === -1 || (currentProjects || 0) < maxProjects;

      return {
        canCreate,
        currentProjects: currentProjects || 0,
        maxProjects,
        planName: limit.plan_name
      };
    } catch (error) {
      // Return a safe default that prevents creating projects
      return {
        canCreate: false,
        currentProjects: 0,
        maxProjects: 0,
        planName: 'unknown'
      };
    }
  }

  /**
   * Create a new project with limit checking
   */
  static async createProject(
    workspaceId: string, 
    projectData: { name: string; description?: string; code?: string }
  ): Promise<ProjectCreationResult> {
    try {
      // Check if we can create a project
      const limitCheck = await this.canCreateProject(workspaceId);
      
      if (!limitCheck.canCreate) {
        const errorMessage = limitCheck.maxProjects === -1 
          ? 'Unable to create project at this time'
          : `Cannot create more projects. You've reached the limit for your ${limitCheck.planName} plan.`;
        
        return {
          success: false,
          error: errorMessage,
          canCreate: false,
          currentProjects: limitCheck.currentProjects,
          maxProjects: limitCheck.maxProjects,
          planName: limitCheck.planName
        };
      }

      // Create the project
      const { data: project, error: projectError } = await (supabase as any)
        .from('products')
        .insert({
          name: projectData.name,
          description: projectData.description || null,
          code: projectData.code || null,
          workspace_id: workspaceId
        })
        .select()
        .single();

      if (projectError) {
        throw projectError;
      }

      return {
        success: true,
        project,
        canCreate: true,
        currentProjects: limitCheck.currentProjects + 1,
        maxProjects: limitCheck.maxProjects,
        planName: limitCheck.planName
      };
    } catch (error) {
      console.error('Error creating project:', error);
      return {
        success: false,
        error: `Failed to create project: ${error.message}`,
        canCreate: false,
        currentProjects: 0,
        maxProjects: 0,
        planName: 'unknown'
      };
    }
  }

  /**
   * Get all projects for a workspace
   */
  static async getWorkspaceProjects(workspaceId: string): Promise<any[]> {
    try {
      const { data, error } = await (supabase as any)
        .from('products')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching workspace projects:', error);
      return [];
    }
  }

  /**
   * Get projects that the current user has access to based on project membership
   */
  static async getUserAccessibleProjects(): Promise<any[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Get current workspace using the proper utility function
      const currentSlug = getCurrentTenantSlug();
      
      if (!currentSlug) return [];

      // Get workspace by slug
      const { data: workspace, error: workspaceError } = await (supabase as any)
        .from('watt_workspaces')
        .select('id')
        .eq('slug', currentSlug)
        .single();

      if (workspaceError || !workspace) {
        console.warn('Workspace not found for slug:', currentSlug);
        return [];
      }

      // Get ONLY projects where the user is a member via project_members table
      const { data: projects, error: projectsError } = await (supabase as any)
        .from('products')
        .select(`
          *,
          project_members!inner(
            user_id,
            role,
            created_at
          )
        `)
        .eq('workspace_id', workspace.id)
        .eq('project_members.user_id', user.id)
        .order('name');

      if (projectsError) throw projectsError;
      return projects || [];
    } catch (error) {
      console.error('Error fetching user accessible projects:', error);
      return [];
    }
  }
} 